/**
 * @Prest0n summon gate — feature flag (W3b, owner directive 2026-08-06).
 *
 * Its own flag, deliberately separate from `THREAD_MENTIONS_*`: that one gates
 * mention AUTOCOMPLETE and people-summoning, which are additive. This one
 * changes when Prest0n SPEAKS, which is the only change in this whole workstream
 * whose failure mode is silence.
 *
 * Silence is the hardest defect to notice — no error, no exception, no red CI,
 * nobody files a bug for a week. Every other fix in this run announced itself:
 * duplicate replies were visible, two avatars were visible, wrong ordering was
 * visible. So this shipped the way the durable-write rollout did: DEFAULT OFF,
 * enable the QA workspaces, prove it, then widen.
 *
 * ── DEFAULT ON (owner directive 2026-08-26) ──────────────────────────────
 * That staging is now complete, and the owner's instruction closes it:
 * "ensure that Prest0n doesn't reply in these situations globally across all
 * group threads unless this toggle option is turned off." A per-account
 * allowlist is the opposite of global — the same toggle would keep reading
 * "Prest0n replies only when mentioned" in every workspace outside it while
 * Prest0n answered anyway, which is the trust break the report was about.
 *
 * Widening the flag is deliberately NOT the whole fix, and would have been a
 * poor one on its own: the reported failure happened INSIDE an enabled
 * account. The two defects behind it are fixed where they live — the composer
 * no longer exempts attachment sends (GlobalChatSystem.tsx), and the rule is
 * now enforced server-side at the turn boundary every surface crosses
 * (functions/src/prest0nSession/summonGate.ts), so the Slack lane, SMS, the VM
 * bridge and routines obey it whatever any browser flag says. This constant
 * governs only the CLIENT's local fast path.
 *
 * The rollback lever survives intact: set this to false and the client gate is
 * inert again, with the allowlist below still able to re-enable named
 * accounts. What CANNOT be rolled back here is the server gate — that is the
 * point of it.
 */
export const THREAD_SUMMON_GATE_ENABLED: boolean = true;

/** Build-time env var (comma-separated account ids) naming the enabled accounts. */
export const THREAD_SUMMON_GATE_ACCOUNTS_ENV = 'REACT_APP_THREAD_SUMMON_GATE_ACCOUNTS';

export interface SummonGateEnablementContext {
  accountId?: string | null;
  /** Explicit override — wins over everything (tests / parent-computed). */
  override?: boolean;
  /** Accounts enabled for the summon gate. Defaults to the build-time allowlist. */
  enabledAccountIds?: Iterable<string>;
}

/**
 * Parse the allowlist from a build-time env map. CRA/webpack only inlines a
 * DIRECT static `process.env.REACT_APP_*` member reference into the browser
 * bundle, so read the specific var by direct reference (tests inject `env`).
 */
export function readEnabledSummonGateAccountsFromEnv(
  env?: Record<string, string | undefined>,
): string[] {
  const raw =
    (env
      ? env[THREAD_SUMMON_GATE_ACCOUNTS_ENV]
      : process.env.REACT_APP_THREAD_SUMMON_GATE_ACCOUNTS) ?? '';
  const seen = new Set<string>();
  for (const part of String(raw).split(',')) {
    const id = part.trim();
    if (id) seen.add(id);
  }
  return [...seen];
}

/**
 * Account-scoped enablement. Precedence: explicit override → blank-account
 * guard → compile default → allowlist → OFF.
 *
 * THE BLANK-ACCOUNT GUARD RUNS FIRST, ahead of the compile default. An
 * anonymous or still-resolving session must never silence Prest0n: it has no
 * membership to read, so the participant count reads 0 and the gate would fall
 * through to "direct chat" anyway — but ordering the guard ahead of the flag
 * keeps that a stated property of this function rather than a coincidence of
 * another module. It was already the rule while the default was OFF (the
 * allowlist sat behind it); flipping the default to ON in 2026-08-26 would
 * have quietly stepped over it.
 */
export function resolveSummonGateEnabled(ctx: SummonGateEnablementContext = {}): boolean {
  if (ctx.override !== undefined) return ctx.override;

  const account = (ctx.accountId ?? '').trim();
  if (!account) return false;

  if (THREAD_SUMMON_GATE_ENABLED) return true;

  const source = ctx.enabledAccountIds ?? readEnabledSummonGateAccountsFromEnv();
  for (const raw of source) {
    const token = String(raw ?? '').trim();
    // '*' admits every RESOLVED account — checked after the blank guard, so
    // unresolved sessions stay fail-open (Prest0n keeps answering).
    if (token === '*' || token === account) return true;
  }
  return false;
}
